import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Stack,
} from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import CustomDataGrid from "components/common/CustomDataGrid";
import GenerateProtocolModal from "components/features/TreatmentPlan/GenerateProtocolModal";
import DashboardLayout from "components/layouts/DashboardLayout";
import {
  TREATMENT_PLAN_CONSTANTS,
  type GeneratedByOption,
  type DateRangeOption,
  type SortOption,
  type SortOrderOption,
} from "constants/treatmentPlan";
import { useToast } from "contexts/ToastContext";
import { format } from "date-fns";
import {
  useTreatmentPlan,
  useTreatmentPlansList,
} from "hooks/useTreatmentPlan";
import { GenerateTreatmentPlanForPatientDto } from "libs/cellera-sdk";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";

const TreatmentPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { generateTreatmentPlan, isGenerating } = useTreatmentPlan();
  const { showSuccess } = useToast();
  const { colors } = useCelleraTheme();

  // Pagination & Filter states
  const [page, setPage] = useState<number>(
    TREATMENT_PLAN_CONSTANTS.DEFAULT_PAGE
  );
  const [pageSize] = useState(TREATMENT_PLAN_CONSTANTS.DEFAULT_PAGE_SIZE);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [generatedBy, setGeneratedBy] = useState<GeneratedByOption>(
    TREATMENT_PLAN_CONSTANTS.GENERATED_BY_OPTIONS.ALL
  );
  const [dateRange, setDateRange] = useState<DateRangeOption>(
    TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS.ALL_TIME
  );
  const [sortBy, setSortBy] = useState<SortOption>(
    TREATMENT_PLAN_CONSTANTS.SORT_OPTIONS.GENERATED_ON
  );
  const [sortOrder, setSortOrder] = useState<SortOrderOption>(
    TREATMENT_PLAN_CONSTANTS.SORT_ORDER_OPTIONS.DESC
  );
  const [showFilters, setShowFilters] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

  const { treatmentPlans, pagination, isLoading } = useTreatmentPlansList({
    page,
    pageSize,
    search: searchQuery.trim() || undefined,
    generatedBy,
    dateRange,
    customStartDate:
      customStartDate &&
      customStartDate instanceof Date &&
      !isNaN(customStartDate.getTime())
        ? format(customStartDate, TREATMENT_PLAN_CONSTANTS.API_DATE_FORMAT)
        : undefined,
    customEndDate:
      customEndDate &&
      customEndDate instanceof Date &&
      !isNaN(customEndDate.getTime())
        ? format(customEndDate, TREATMENT_PLAN_CONSTANTS.API_DATE_FORMAT)
        : undefined,
    sortBy,
    sortOrder,
  });

  const [isGenerateProtocolModalOpen, setIsGenerateProtocolModalOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<{
    patientId: string;
    treatmentId: string;
  } | null>(null);

  // Use treatmentPlans directly - no transformation needed
  const filteredData = treatmentPlans || [];

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    patientId: string,
    treatmentId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow({ patientId, treatmentId });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleViewDetails = () => {
    if (selectedRow) {
      navigate(
        `${TREATMENT_PLAN_CONSTANTS.ROUTES.GENERATED_RESULT}/${selectedRow.patientId}/${selectedRow.treatmentId}`
      );
    }
    handleMenuClose();
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: TREATMENT_PLAN_CONSTANTS.COLUMN_HEADERS.PROTOCOL_FOLDER_NAME,
      flex: 1,
      minWidth: 200,
      headerAlign: "left",
      renderCell: (params) =>
        `${TREATMENT_PLAN_CONSTANTS.PROTOCOL_PREFIX}${params.value.slice(0, TREATMENT_PLAN_CONSTANTS.PROTOCOL_ID_LENGTH)}`,
    },
    {
      field: "createdAt",
      headerName: TREATMENT_PLAN_CONSTANTS.COLUMN_HEADERS.GENERATED_ON,
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      renderCell: (params) =>
        format(new Date(params.value), TREATMENT_PLAN_CONSTANTS.DATE_FORMAT),
    },
    {
      field: "patientName",
      headerName: TREATMENT_PLAN_CONSTANTS.COLUMN_HEADERS.PATIENT_NAME,
      flex: 1,
      minWidth: 200,
      headerAlign: "left",
      renderCell: (params) => {
        // Try to get patient name from patient data, fallback to patientId
        const patientName =
          params.row.patient?.fullName || params.row.patient?.firstName;
        return patientName || "Unknown Patient";
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: TREATMENT_PLAN_CONSTANTS.COLUMN_HEADERS.ACTIONS,
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      headerAlign: "center",
      cellClassName: "actions",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      getActions: ({ row }) => [
        <GridActionsCellItem
          key="more-actions"
          icon={<MoreVertIcon />}
          label="More Actions"
          onClick={(event) => handleMenuOpen(event, row.patientId, row.id)}
        />,
      ],
    },
  ];

  const handleGenerateProtocolClick = () => {
    setIsGenerateProtocolModalOpen(true);
  };

  const handleGenerateProtocolClose = () => {
    setIsGenerateProtocolModalOpen(false);
  };

  const handleManualRefresh = async () => {
    try {
      setIsRefreshing(true);
      setRefreshKey((prev) => prev + 1);
      await queryClient.invalidateQueries({
        queryKey: TREATMENT_PLAN_CONSTANTS.QUERY_KEYS.TREATMENT_PLANS,
      });
    } catch (error) {
      console.error("Error manual refresh failed:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleGenerateProtocol = async (
    params: GenerateTreatmentPlanForPatientDto
  ) => {
    try {
      const response = await generateTreatmentPlan(params);
      handleGenerateProtocolClose();

      // Reload table
      setRefreshKey((prev) => prev + 1);
      queryClient.invalidateQueries({
        queryKey: TREATMENT_PLAN_CONSTANTS.QUERY_KEYS.TREATMENT_PLANS,
      });

      // Navigate directly to result page using the treatment_plan_id from response
      if (response?.treatment_plan_id) {
        navigate(
          `${TREATMENT_PLAN_CONSTANTS.ROUTES.GENERATED_RESULT}/${params.patientId}/${response.treatment_plan_id}`
        );
      } else {
        // Show success alert if navigation failed
        showSuccess(
          TREATMENT_PLAN_CONSTANTS.MESSAGES.SUCCESS.TITLE,
          TREATMENT_PLAN_CONSTANTS.MESSAGES.SUCCESS.GENERATION
        );
      }
    } catch (error) {
      console.error("Failed to generate treatment plan:", error);
      handleGenerateProtocolClose();
    }
  };

  // Filter and sort handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    resetPagination(); // Reset to first page when searching
  };

  const handleGeneratedByChange = (value: GeneratedByOption) => {
    setGeneratedBy(value);
    resetPagination();
  };

  const handleDateRangeChange = (value: DateRangeOption) => {
    setDateRange(value);
    resetPagination();
  };

  const handleSortChange = (field: SortOption) => {
    if (sortBy === field) {
      setSortOrder(
        sortOrder === TREATMENT_PLAN_CONSTANTS.SORT_ORDER_OPTIONS.ASC
          ? TREATMENT_PLAN_CONSTANTS.SORT_ORDER_OPTIONS.DESC
          : TREATMENT_PLAN_CONSTANTS.SORT_ORDER_OPTIONS.ASC
      );
    } else {
      setSortBy(field);
      setSortOrder(TREATMENT_PLAN_CONSTANTS.SORT_ORDER_OPTIONS.DESC);
    }
    resetPagination();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setGeneratedBy(TREATMENT_PLAN_CONSTANTS.GENERATED_BY_OPTIONS.ALL);
    setDateRange(TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS.ALL_TIME);
    setCustomStartDate(null);
    setCustomEndDate(null);
    setSortBy(TREATMENT_PLAN_CONSTANTS.SORT_OPTIONS.GENERATED_ON);
    setSortOrder(TREATMENT_PLAN_CONSTANTS.SORT_ORDER_OPTIONS.DESC);
    resetPagination();
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (page > TREATMENT_PLAN_CONSTANTS.DEFAULT_PAGE) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination && pagination.hasMore) {
      setPage(page + 1);
    }
  };

  // Reset pagination when filters change
  const resetPagination = () => {
    setPage(TREATMENT_PLAN_CONSTANTS.DEFAULT_PAGE);
  };

  return (
    <DashboardLayout
      title="Treatment Plans"
      sideButton={
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            sx={{
              borderRadius: TREATMENT_PLAN_CONSTANTS.BORDER_RADIUS,
              borderColor: colors.primary.main,
              color: colors.primary.main,
              backgroundColor: colors.primary.light,
              "&:hover": {
                borderColor: colors.primary.dark,
                backgroundColor: colors.primary.light,
                color: colors.primary.dark,
              },
              "&:disabled": {
                borderColor: colors.primary.main,
                color: colors.primary.main,
                opacity: 0.6,
              },
            }}
          >
            {isRefreshing
              ? TREATMENT_PLAN_CONSTANTS.MESSAGES.LOADING.REFRESHING
              : TREATMENT_PLAN_CONSTANTS.MESSAGES.BUTTONS.REFRESH}
          </Button>
          <Button
            variant="contained"
            className="rounded-full px-6 py-2 font-inter text-white transition-all duration-200 hover:shadow-lg"
            startIcon={<AddIcon />}
            onClick={handleGenerateProtocolClick}
            disabled={isGenerating}
          >
            {isGenerating
              ? TREATMENT_PLAN_CONSTANTS.MESSAGES.LOADING.GENERATING
              : TREATMENT_PLAN_CONSTANTS.MESSAGES.BUTTONS.GENERATE_PROTOCOL}
          </Button>
        </Box>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          p: 3,
        }}
      >
        {/* Search and Filter Section */}
        <Box sx={{ mb: TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM }}>
          <Stack
            direction="row"
            spacing={TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM}
            alignItems="center"
            sx={{ mb: TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM }}
          >
            <TextField
              variant="outlined"
              placeholder={TREATMENT_PLAN_CONSTANTS.SEARCH_PLACEHOLDER}
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                flex: 1,
                maxWidth: TREATMENT_PLAN_CONSTANTS.MIN_WIDTH.SEARCH_FIELD,
                "& .MuiOutlinedInput-root": {
                  borderRadius: TREATMENT_PLAN_CONSTANTS.BORDER_RADIUS,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                borderRadius: TREATMENT_PLAN_CONSTANTS.BORDER_RADIUS,
                borderColor: colors.primary.main,
                color: colors.primary.main,
                backgroundColor: colors.primary.light,
                "&:hover": {
                  borderColor: colors.primary.dark,
                  backgroundColor: colors.primary.light,
                  color: colors.primary.dark,
                },
              }}
            >
              {TREATMENT_PLAN_CONSTANTS.MESSAGES.BUTTONS.FILTERS}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              onClick={() =>
                handleSortChange(
                  TREATMENT_PLAN_CONSTANTS.SORT_OPTIONS.GENERATED_ON
                )
              }
              sx={{
                borderRadius: TREATMENT_PLAN_CONSTANTS.BORDER_RADIUS,
                borderColor: colors.primary.main,
                color: colors.primary.main,
                backgroundColor: colors.primary.light,
                "&:hover": {
                  borderColor: colors.primary.dark,
                  backgroundColor: colors.primary.light,
                  color: colors.primary.dark,
                },
              }}
            >
              Sort{" "}
              {sortOrder === TREATMENT_PLAN_CONSTANTS.SORT_ORDER_OPTIONS.DESC
                ? "↓"
                : "↑"}
            </Button>
          </Stack>

          {/* Advanced Filters */}
          {showFilters && (
            <Box
              sx={{
                p: TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM,
                border: `1px solid ${TREATMENT_PLAN_CONSTANTS.COLORS.BORDER_COLOR}`,
                borderRadius: TREATMENT_PLAN_CONSTANTS.BORDER_RADIUS,
                backgroundColor:
                  TREATMENT_PLAN_CONSTANTS.COLORS.BACKGROUND_LIGHT,
                mb: TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM,
              }}
            >
              <Stack
                direction="row"
                spacing={TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM}
                alignItems="center"
                flexWrap="wrap"
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: TREATMENT_PLAN_CONSTANTS.MIN_WIDTH.FORM_CONTROL,
                  }}
                >
                  <InputLabel>
                    {TREATMENT_PLAN_CONSTANTS.MESSAGES.LABELS.GENERATED_BY}
                  </InputLabel>
                  <Select
                    value={generatedBy}
                    label="Generated By"
                    onChange={(e) =>
                      handleGeneratedByChange(
                        e.target.value as GeneratedByOption
                      )
                    }
                  >
                    <MenuItem
                      value={TREATMENT_PLAN_CONSTANTS.GENERATED_BY_OPTIONS.ALL}
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.ALL}
                    </MenuItem>
                    <MenuItem
                      value={TREATMENT_PLAN_CONSTANTS.GENERATED_BY_OPTIONS.YOU}
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.YOU}
                    </MenuItem>
                    <MenuItem
                      value={
                        TREATMENT_PLAN_CONSTANTS.GENERATED_BY_OPTIONS.PRACTICE
                      }
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.PRACTICE}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    minWidth: TREATMENT_PLAN_CONSTANTS.MIN_WIDTH.FORM_CONTROL,
                  }}
                >
                  <InputLabel>
                    {TREATMENT_PLAN_CONSTANTS.MESSAGES.LABELS.DATE_RANGE}
                  </InputLabel>
                  <Select
                    value={dateRange}
                    label="Date Range"
                    onChange={(e) =>
                      handleDateRangeChange(e.target.value as DateRangeOption)
                    }
                  >
                    <MenuItem
                      value={
                        TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS.ALL_TIME
                      }
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.ALL_TIME}
                    </MenuItem>
                    <MenuItem
                      value={
                        TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS.LAST_7_DAYS
                      }
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.LAST_7_DAYS}
                    </MenuItem>
                    <MenuItem
                      value={
                        TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS
                          .LAST_3_MONTHS
                      }
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.LAST_3_MONTHS}
                    </MenuItem>
                    <MenuItem
                      value={
                        TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS
                          .LAST_6_MONTHS
                      }
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.LAST_6_MONTHS}
                    </MenuItem>
                    <MenuItem
                      value={
                        TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS
                          .LAST_12_MONTHS
                      }
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.LAST_12_MONTHS}
                    </MenuItem>
                    <MenuItem
                      value={
                        TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS.CUSTOM_RANGE
                      }
                    >
                      {TREATMENT_PLAN_CONSTANTS.MESSAGES.OPTIONS.CUSTOM_RANGE}
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Custom Date Range Pickers */}
                {dateRange ===
                  TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS.CUSTOM_RANGE && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack
                      direction="row"
                      spacing={TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM}
                      alignItems="center"
                    >
                      <DatePicker
                        label={
                          TREATMENT_PLAN_CONSTANTS.MESSAGES.LABELS.START_DATE
                        }
                        value={customStartDate}
                        onChange={(newValue) => {
                          setCustomStartDate(newValue);
                          resetPagination();
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              minWidth:
                                TREATMENT_PLAN_CONSTANTS.MIN_WIDTH.DATE_PICKER,
                            },
                            error: Boolean(
                              customStartDate &&
                                isNaN(customStartDate.getTime())
                            ),
                            helperText:
                              customStartDate &&
                              isNaN(customStartDate.getTime())
                                ? TREATMENT_PLAN_CONSTANTS.MESSAGES.ERRORS
                                    .INVALID_DATE
                                : "",
                          },
                        }}
                      />
                      <DatePicker
                        label={
                          TREATMENT_PLAN_CONSTANTS.MESSAGES.LABELS.END_DATE
                        }
                        value={customEndDate}
                        onChange={(newValue) => {
                          setCustomEndDate(newValue);
                          resetPagination();
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              minWidth:
                                TREATMENT_PLAN_CONSTANTS.MIN_WIDTH.DATE_PICKER,
                            },
                            error: Boolean(
                              customEndDate && isNaN(customEndDate.getTime())
                            ),
                            helperText:
                              customEndDate && isNaN(customEndDate.getTime())
                                ? TREATMENT_PLAN_CONSTANTS.MESSAGES.ERRORS
                                    .INVALID_DATE
                                : "",
                          },
                        }}
                      />
                    </Stack>
                  </LocalizationProvider>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearFilters}
                  sx={{ borderRadius: TREATMENT_PLAN_CONSTANTS.BORDER_RADIUS }}
                >
                  {TREATMENT_PLAN_CONSTANTS.MESSAGES.BUTTONS.CLEAR_FILTERS}
                </Button>
              </Stack>

              {/* Active Filters Display */}
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {searchQuery && (
                    <Chip
                      label={`Search: ${searchQuery}`}
                      onDelete={() => setSearchQuery("")}
                      size="small"
                    />
                  )}
                  {generatedBy !==
                    TREATMENT_PLAN_CONSTANTS.GENERATED_BY_OPTIONS.ALL && (
                    <Chip
                      label={`Generated By: ${generatedBy}`}
                      onDelete={() =>
                        setGeneratedBy(
                          TREATMENT_PLAN_CONSTANTS.GENERATED_BY_OPTIONS.ALL
                        )
                      }
                      size="small"
                    />
                  )}
                  {dateRange !==
                    TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS.ALL_TIME && (
                    <Chip
                      label={`Date Range: ${dateRange.replace(/_/g, " ")}`}
                      onDelete={() =>
                        setDateRange(
                          TREATMENT_PLAN_CONSTANTS.DATE_RANGE_OPTIONS.ALL_TIME
                        )
                      }
                      size="small"
                    />
                  )}
                  {customStartDate &&
                    customStartDate instanceof Date &&
                    !isNaN(customStartDate.getTime()) && (
                      <Chip
                        label={`From: ${format(customStartDate, TREATMENT_PLAN_CONSTANTS.DATE_FORMAT_SHORT)}`}
                        onDelete={() => setCustomStartDate(null)}
                        size="small"
                      />
                    )}
                  {customEndDate &&
                    customEndDate instanceof Date &&
                    !isNaN(customEndDate.getTime()) && (
                      <Chip
                        label={`To: ${format(customEndDate, TREATMENT_PLAN_CONSTANTS.DATE_FORMAT_SHORT)}`}
                        onDelete={() => setCustomEndDate(null)}
                        size="small"
                      />
                    )}
                </Stack>
              </Box>
            </Box>
          )}
        </Box>

        {/* Data Grid */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <CustomDataGrid
            key={refreshKey}
            rows={filteredData}
            columns={columns}
            loading={isLoading}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: TREATMENT_PLAN_CONSTANTS.DEFAULT_PAGE_SIZE,
                },
              },
            }}
            pageSizeOptions={[TREATMENT_PLAN_CONSTANTS.DEFAULT_PAGE_SIZE]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            hideFooterPagination
            sx={{
              height: "100%",
              "& .MuiDataGrid-columnHeader": {
                fontWeight: "bold",
              },
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-main": {
                overflow: "auto",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflow: "auto",
              },
              // Responsive adjustments
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor:
                  TREATMENT_PLAN_CONSTANTS.COLORS.BACKGROUND_LIGHT,
                borderBottom: `2px solid ${TREATMENT_PLAN_CONSTANTS.COLORS.BORDER_COLOR}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                fontSize: TREATMENT_PLAN_CONSTANTS.FONT_SIZES.MEDIUM,
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${TREATMENT_PLAN_CONSTANTS.COLORS.BORDER_COLOR}`,
                fontSize: TREATMENT_PLAN_CONSTANTS.FONT_SIZES.MEDIUM,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor:
                  TREATMENT_PLAN_CONSTANTS.COLORS.BACKGROUND_HOVER,
              },
              // Zoom-friendly adjustments
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                minWidth: "fit-content",
              },
              "& .MuiDataGrid-cellContent": {
                minWidth: "fit-content",
              },
              // Mobile responsive
              [`@media (max-width: ${TREATMENT_PLAN_CONSTANTS.BREAKPOINTS.MOBILE}px)`]:
                {
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: TREATMENT_PLAN_CONSTANTS.FONT_SIZES.SMALL,
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: TREATMENT_PLAN_CONSTANTS.FONT_SIZES.SMALL,
                    padding: "8px 4px",
                  },
                },
              // High zoom level adjustments
              [`@media (min-resolution: ${TREATMENT_PLAN_CONSTANTS.BREAKPOINTS.HIGH_RESOLUTION})`]:
                {
                  "& .MuiDataGrid-cell": {
                    fontSize: TREATMENT_PLAN_CONSTANTS.FONT_SIZES.MEDIUM,
                    padding: "12px 16px",
                  },
                },
              // Actions column specific styling
              "& .MuiDataGrid-cell--actions": {
                padding: "8px 4px",
                justifyContent: "center",
                "& .MuiIconButton-root": {
                  padding: "4px",
                  minWidth: "auto",
                },
              },
            }}
          />
        </Box>

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM,
            px: TREATMENT_PLAN_CONSTANTS.SPACING.SMALL,
            py: TREATMENT_PLAN_CONSTANTS.SPACING.SMALL,
            backgroundColor: TREATMENT_PLAN_CONSTANTS.COLORS.BACKGROUND_LIGHT,
            borderRadius: TREATMENT_PLAN_CONSTANTS.SPACING.SMALL,
            border: `1px solid ${TREATMENT_PLAN_CONSTANTS.COLORS.BORDER_COLOR}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: TREATMENT_PLAN_CONSTANTS.COLORS.TEXT_SECONDARY,
              fontWeight: 500,
            }}
          >
            {TREATMENT_PLAN_CONSTANTS.MESSAGES.LABELS.ROWS_PER_PAGE}: {pageSize}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: TREATMENT_PLAN_CONSTANTS.SPACING.MEDIUM,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: TREATMENT_PLAN_CONSTANTS.COLORS.TEXT_SECONDARY,
                fontWeight: 500,
              }}
            >
              {pagination
                ? `${(pagination.page - 1) * pagination.pageSize + 1}-${Math.min(page * pageSize, filteredData.length)} of ${pagination.total}`
                : `1-${filteredData.length} of ${filteredData.length}`}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handlePreviousPage}
                disabled={page === TREATMENT_PLAN_CONSTANTS.DEFAULT_PAGE}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: TREATMENT_PLAN_CONSTANTS.SPACING.SMALL,
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor:
                      TREATMENT_PLAN_CONSTANTS.COLORS.BACKGROUND_HOVER,
                  },
                  "&:disabled": {
                    backgroundColor:
                      TREATMENT_PLAN_CONSTANTS.COLORS.BACKGROUND_HOVER,
                    color: TREATMENT_PLAN_CONSTANTS.COLORS.TEXT_DISABLED,
                  },
                }}
              >
                {"<"}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={!pagination?.hasMore}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: TREATMENT_PLAN_CONSTANTS.SPACING.SMALL,
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor:
                      TREATMENT_PLAN_CONSTANTS.COLORS.BACKGROUND_HOVER,
                  },
                  "&:disabled": {
                    backgroundColor:
                      TREATMENT_PLAN_CONSTANTS.COLORS.BACKGROUND_HOVER,
                    color: TREATMENT_PLAN_CONSTANTS.COLORS.TEXT_DISABLED,
                  },
                }}
              >
                {">"}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Generate Protocol Modal */}
        {isGenerateProtocolModalOpen && (
          <GenerateProtocolModal
            isGenerating={isGenerating}
            open={isGenerateProtocolModalOpen}
            onClose={handleGenerateProtocolClose}
            onGenerate={handleGenerateProtocol}
          />
        )}

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleViewDetails}>
            {TREATMENT_PLAN_CONSTANTS.MESSAGES.BUTTONS.VIEW_DETAILS}
          </MenuItem>
        </Menu>
      </Box>
    </DashboardLayout>
  );
};

export default TreatmentPlanPage;
